import { useCallback, useEffect, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate, useSearchParams } from 'react-router'
import { authenticationState } from '@repo/unicity'
import { getPaymentResponseGatewayUrls } from '@/core/env.config'
import { checkoutUuidAtom } from '@/core/checkout/checkout.store'
import { cartAtom } from '@/core/cart/cart.store'
import { analytics, type AnalyticsProps } from '@/core/analytics/analytics'
import { newAttemptId, rememberAttempt } from '@/core/analytics/checkout-attempt'
import { getTaiwanFormat, createTaiwanHostedPayment, checkTaiwanDuplicateOrder, putTaiwanPaymentOption } from '../services/taiwan.api'
import {
    taiwanFormatDataAtom,
    taiwanPaymentMethodAtom,
    taiwanPaymentLoadingAtom,
    taiwanPaymentErrorsAtom,
    taiwanRedirectFormAtom,
    taiwanDuplicateOrderAtom
} from '../stores/taiwan.store'

// Reads the authoritative order total from the loaded detail-v2 format. The
// value can be a number, numeric string, or a { value, suffix } record.
const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {})
const readFormatTotal = (format: Record<string, unknown> | null): number => {
    const u23 = asRecord(asRecord(asRecord(format ?? {}).ushop).u23)
    const tp = asRecord(u23.formatTotal).total_price
    if (typeof tp === 'number') return tp
    if (typeof tp === 'string') return Number(tp) || 0
    const value = asRecord(tp).value
    return typeof value === 'number' ? value : Number(value) || 0
}
import type { TaiwanHostedPaymentRequest } from '../types/taiwan.types'

// TW is a redirect-payment market: the payment step prepares the order via the
// payment router (proxy/insert/TWN) and hands the customer off to a hosted
// gateway (2c2p). No card details are collected inline. The response either
// relocates the browser (window.location) or is auto-submitted as a hidden form.
export const useTaiwanPayment = () => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()

    const auth = useAtomValue(authenticationState)
    const method = useAtomValue(taiwanPaymentMethodAtom)
    const cart = useAtomValue(cartAtom)
    const setUuid = useSetAtom(checkoutUuidAtom)
    const [format, setFormat] = useAtom(taiwanFormatDataAtom)
    const [loading, setLoading] = useAtom(taiwanPaymentLoadingAtom)
    const [errors, setErrors] = useAtom(taiwanPaymentErrorsAtom)
    const [redirectForm, setRedirectForm] = useAtom(taiwanRedirectFormAtom)
    const [duplicateOrder, setDuplicateOrder] = useAtom(taiwanDuplicateOrderAtom)

    const [initialized, setInitialized] = useState(false)
    const [loadError, setLoadError] = useState<string[]>([])

    const isAuthenticated = !!(auth.token && auth.baId)

    // ─── Tracing ─────────────────────────────────────────────────────────────────
    //
    // One id per pay attempt, stamped on every event from purchaseClicked onward so the whole
    // hand-off (click → duplicate check → create → gateway redirect → /payment-response) reads
    // back as one trace.
    const attemptIdRef = useRef('')

    const trackCheckout = useCallback(
        (event: Parameters<typeof analytics.track>[0], props?: AnalyticsProps) =>
            analytics.track(event, {
                checkout_attempt_id: attemptIdRef.current || undefined,
                payment_method: method,
                cart_item_count: Object.keys(cart).length,
                ...props
            }),
        [method, cart]
    )

    // ─── Load the order format for the active UUID ───────────────────────────────
    // Guards against React StrictMode's double-mount (dev) firing getFormat twice.
    const loadStartedRef = useRef(false)
    useEffect(() => {
        if (loadStartedRef.current) return
        loadStartedRef.current = true

        const uuid = searchParams.get('uuid')
        if (!uuid) {
            navigate('/checkout', { replace: true })
            return
        }

        setUuid(uuid)
        setErrors([])
        setRedirectForm(null)
        setDuplicateOrder(null)
        setLoadError([])
        ;(async () => {
            try {
                const format = await getTaiwanFormat(uuid)
                setFormat({ ...format, uuid })
            } catch {
                analytics.track('checkoutFormatLoadFailed', { step: 'load', uuid })
                setLoadError(['failed_to_load_payment_data'])
            } finally {
                setInitialized(true)
            }
        })()
        // Load once on mount; deps kept minimal on purpose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ─── Prepare hosted payment → redirect to gateway ────────────────────────────
    const submitHostedPayment = useCallback(
        async (uuid: string) => {
            const isCod = method === 'cashOnDelivery'

            // COD: tag the format with the payment option first (best-effort — the
            // payments[] below is what actually settles the order as COD).
            if (isCod) {
                try {
                    await putTaiwanPaymentOption(uuid, JSON.stringify(cart), 'cashondelivery')
                } catch {
                    // Non-fatal — proceed to settle the order.
                    trackCheckout('checkoutFormatLoadFailed', { step: 'cod_payment_option', uuid })
                }
            }

            const body: TaiwanHostedPaymentRequest = {
                uuid,
                ...getPaymentResponseGatewayUrls(),
                source_path: window.location.href,
                type: 'feelGreat',
                // TW guests are auto-logged-in during enrollment, so a member session
                // is expected here; still send identity only when authenticated.
                ...(isAuthenticated ? { ba_id: auth.baId ?? '', token: auth.token ?? '' } : {})
            }
            if (isCod) {
                body.payments = [{ method: 'cashOnDelivery', amount: readFormatTotal(format) }]
                if (!isAuthenticated) body.order_type = 'enroll'
            }

            trackCheckout('checkoutPaymentCreateStarted', { uuid })
            const res = await createTaiwanHostedPayment(body)
            rememberAttempt(res.referenceNo ?? '', attemptIdRef.current)

            if (res.success === false) {
                trackCheckout('checkoutPaymentCreateFailed', { uuid, reason: 'create_payment', error_code: res.error?.message })
                setErrors([res.error?.message || 'failed_to_load_payment_data'])
                setLoading(false)
                return
            }

            // COD settles server-side (no card redirect). Go straight to the success
            // page with the returned referenceNo so it can poll the payment status.
            if (isCod) {
                if (!res.referenceNo) {
                    trackCheckout('checkoutPaymentCreateFailed', { uuid, reason: 'cod_no_reference' })
                    setErrors(['failed_to_load_payment_data'])
                    setLoading(false)
                    return
                }
                trackCheckout('checkoutPaymentNextActionReceived', { gateway_ref_id: res.referenceNo, next_action: 'cod_settled' })
                navigate(`/payment-response/success?gateway_ref_id=${res.referenceNo}`)
                return
            }

            const gateway = res.data
            if (!gateway) {
                trackCheckout('checkoutPaymentCreateFailed', { uuid, reason: 'no_gateway_data', error_code: res.error?.message })
                setErrors([res.error?.message || 'failed_to_load_payment_data'])
                setLoading(false)
                return
            }

            // `relocation` → straight browser redirect; otherwise auto-submit the
            // hidden form (RedirectPaymentGateway). Keep `loading` true through the
            // navigation so the button never re-enables before the page unloads.
            if (gateway.relocation && gateway.payment_action_url) {
                trackCheckout('checkoutPaymentNextActionReceived', { gateway_ref_id: res.referenceNo ?? '', next_action: 'relocation' })
                window.location.href = gateway.payment_action_url
                return
            }

            const actionUrl = gateway.payment_action_url || gateway.PAYMENT_URL
            if (!actionUrl) {
                trackCheckout('checkoutPaymentCreateFailed', { uuid, reason: 'no_action_url' })
                setErrors(['failed_to_load_payment_data'])
                setLoading(false)
                return
            }
            trackCheckout('checkoutPaymentNextActionReceived', { gateway_ref_id: res.referenceNo ?? '', next_action: 'gateway_form' })
            setRedirectForm({ ...gateway, payment_action_url: actionUrl })
        },
        [auth.baId, auth.token, isAuthenticated, method, cart, format, navigate, setErrors, setLoading, setRedirectForm, trackCheckout]
    )

    // Synchronous guard against a double-click landing before `loading` commits.
    const submittingRef = useRef(false)

    const submitPayment = useCallback(async () => {
        const uuid = searchParams.get('uuid')
        if (!uuid || submittingRef.current) return
        submittingRef.current = true
        setErrors([])
        setLoading(true)
        attemptIdRef.current = newAttemptId()
        trackCheckout('checkoutPurchaseClicked')
        try {
            // Duplicate-order guard: on a hit, surface the confirm dialog and stop.
            try {
                const dup = await checkTaiwanDuplicateOrder(uuid)
                if (dup.is_duplicate) {
                    trackCheckout('checkoutDuplicateDetected', { uuid, payments_id: dup.payments_id })
                    setDuplicateOrder(dup)
                    setLoading(false)
                    return
                }
            } catch {
                // Duplicate check is best-effort — proceed on failure.
            }
            await submitHostedPayment(uuid)
        } catch {
            trackCheckout('checkoutPaymentCreateFailed', { uuid, reason: 'exception' })
            setErrors(['failed_to_load_payment_data'])
            setLoading(false)
        } finally {
            submittingRef.current = false
        }
    }, [searchParams, submitHostedPayment, setDuplicateOrder, setErrors, setLoading, trackCheckout])

    // Proceed despite a duplicate-order warning.
    const confirmDuplicateProceed = useCallback(async () => {
        const uuid = searchParams.get('uuid')
        if (!uuid) return
        setDuplicateOrder(null)
        setErrors([])
        setLoading(true)
        // Same attempt id: the shopper confirmed the same purchase, so the duplicate prompt
        // and what follows stay on one trace.
        trackCheckout('checkoutPurchaseClicked', { after_duplicate: true })
        try {
            await submitHostedPayment(uuid)
        } catch {
            trackCheckout('checkoutPaymentCreateFailed', { uuid, reason: 'exception' })
            setErrors(['failed_to_load_payment_data'])
            setLoading(false)
        }
    }, [searchParams, submitHostedPayment, setDuplicateOrder, setErrors, setLoading, trackCheckout])

    const cancelDuplicate = useCallback(() => {
        setDuplicateOrder(null)
        setLoading(false)
    }, [setDuplicateOrder, setLoading])

    return {
        initialized,
        loadError,
        loading,
        errors,
        redirectForm,
        duplicateOrder,
        submitPayment,
        confirmDuplicateProceed,
        cancelDuplicate
    }
}
